import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useParams, useNavigate } from 'react-router-dom';
import Sidebar from './features/files/components/Sidebar/Sidebar';
import TaskTable from './features/tasks/components/TaskTable/TaskTable';
import UserMenu from './features/auth/components/UserMenu/UserMenu';
import SignupLoginModal from './features/auth/components/SignupLoginModal/SignupLoginModal';
import Menu from './ui/components/Menu/Menu';
import MenuItem from './ui/components/Menu/MenuItem';
import FeedbackModal from './ui/components/FeedbackModal/FeedbackModal';
import AboutModal from './ui/components/AboutModal/AboutModal';
import InviteCodeModal from './features/auth/components/InviteCodeModal/InviteCodeModal';
import FeatureShowcase from './ui/components/FeatureShowcase/FeatureShowcase';
import { TasksProvider } from './shared/contexts/TasksContext';
import { HelpShowcaseProvider } from './shared/contexts/HelpShowcaseContext';
import { AuthProvider } from './features/auth/contexts/AuthContext';
import { FilterProvider, useFilterContext } from './features/filters/contexts/FilterContext';
import { PrioritizeSectionsProvider, usePrioritizeSectionsContext } from './features/filters/contexts/PrioritizeSectionsContext';
import { ImportPopupProvider } from './shared/contexts/ImportPopupContext';
import { PRIORITIZE_SECTIONS } from './features/filters/constants/prioritizeSections';
import { useTasksContext } from './shared/contexts/TasksContext';
import { useAuth } from './features/auth/contexts/AuthContext';
import { getPresetSlugFromPath } from './shared/utils/presets';
import { SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS } from './shared/constants/featureFlags';
import { trackEvent } from './shared/utils/analytics';
import './App.css';

function AppContent() {
  const { isLoggedIn, isLoading, userEmail, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { filter, setFilter, timeFilter, setTimeFilter } = useFilterContext();
  const { currentFileId, listAccessDenied } = useTasksContext();
  const { visiblePrioritizeSections, togglePrioritizeSectionVisibility } = usePrioritizeSectionsContext();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Redirect to / when user is not allowed to view the list (not owner or not logged in)
  useEffect(() => {
    if (listAccessDenied) {
      navigate('/', { replace: true });
    }
  }, [listAccessDenied, navigate]);

  // When logged in and on home path, redirect to the current list URL so /list/:id is canonical
  useEffect(() => {
    if (isLoggedIn && !isLoading && location.pathname === '/' && currentFileId != null && currentFileId !== 'preset' && !String(currentFileId).startsWith('preset:')) {
      navigate(`/list/${currentFileId}`, { replace: true });
    }
  }, [isLoggedIn, isLoading, location.pathname, currentFileId, navigate]);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTaskViewMenuOpen, setIsTaskViewMenuOpen] = useState(false);
  const [isPrioritizeMenuOpen, setIsPrioritizeMenuOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isInviteCodeModalOpen, setIsInviteCodeModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isHelpShowcaseOpen, setIsHelpShowcaseOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const userPillRef = useRef(null);
  const taskViewMenuRef = useRef(null);
  const prioritizeMenuRef = useRef(null);
  const taskViewLongPressTimerRef = useRef(null);
  const prioritizeLongPressTimerRef = useRef(null);
  const taskViewJustLongPressedRef = useRef(false);
  const prioritizeJustLongPressedRef = useRef(false);

  const LONG_PRESS_MS = 400;

  const clearTaskViewLongPressTimer = () => {
    if (taskViewLongPressTimerRef.current != null) {
      clearTimeout(taskViewLongPressTimerRef.current);
      taskViewLongPressTimerRef.current = null;
    }
  };

  const clearPrioritizeLongPressTimer = () => {
    if (prioritizeLongPressTimerRef.current != null) {
      clearTimeout(prioritizeLongPressTimerRef.current);
      prioritizeLongPressTimerRef.current = null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userPillRef.current && !userPillRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (taskViewMenuRef.current && !taskViewMenuRef.current.contains(event.target)) {
        setIsTaskViewMenuOpen(false);
      }
      if (prioritizeMenuRef.current && !prioritizeMenuRef.current.contains(event.target)) {
        setIsPrioritizeMenuOpen(false);
      }
    };

    if (isUserMenuOpen || isTaskViewMenuOpen || isPrioritizeMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isUserMenuOpen, isTaskViewMenuOpen, isPrioritizeMenuOpen]);
  
  if (isLoading) {
    return null; // Or a loading spinner
  }

  const handleHelpClick = () => {
    setIsUserMenuOpen(false);
    setIsHelpShowcaseOpen(true);
  };

  const handleAboutClick = () => {
    setIsUserMenuOpen(false);
    setIsAboutModalOpen(true);
  };

  const handleFeedbackClick = () => {
    setIsUserMenuOpen(false);
    setIsFeedbackOpen(true);
  };

  const handleMenuClick = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };
  
  const handleSidebarClose = () => {
    setIsMobileSidebarOpen(false);
  };
  
  const content = (
    <>
      <div className="app-container">
        <div className="mobile-header">
          <button className="mobile-menu-button text-non-selectable" onClick={handleMenuClick} aria-label="Toggle menu">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 5H18M2 10H18M2 15H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <div className="mobile-header-icon-slot" aria-hidden="true" />
          <div className="mobile-filter-buttons mobile-filter-buttons-center">
            <div className="mobile-filter-buttons-inner">
            <div className="mobile-filter-button-container" ref={taskViewMenuRef}>
              <button
                className={`mobile-filter-button text-non-selectable mobile-filter-button-task-view ${filter === 'all' || filter === 'incomplete' ? 'active' : ''}`}
                onClick={() => {
                  trackEvent('organize_clicked');
                  if (taskViewJustLongPressedRef.current) {
                    taskViewJustLongPressedRef.current = false;
                    return;
                  }
                  if (filter === 'all' || filter === 'incomplete') {
                    // On mobile, dropdown opens only via long-press; tap does nothing when active
                  } else {
                    setFilter('all');
                  }
                }}
                onTouchStart={() => {
                  if (SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS && (filter === 'all' || filter === 'incomplete')) {
                    clearTaskViewLongPressTimer();
                    taskViewLongPressTimerRef.current = setTimeout(() => {
                      taskViewLongPressTimerRef.current = null;
                      taskViewJustLongPressedRef.current = true;
                      setIsTaskViewMenuOpen(true);
                    }, LONG_PRESS_MS);
                  }
                }}
                onTouchEnd={clearTaskViewLongPressTimer}
                onTouchCancel={clearTaskViewLongPressTimer}
                aria-label="Organize"
              >
                <svg width="20" height="20" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="6" height="6" x="10" y="26" fill="currentColor" rx="1" ry="1"/>
                  <path fill="currentColor" d="M15 16h-4a1 1 0 0 0-1 1v1.2H5.8V12H7a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h1.2v17.8H11a.8.8 0 1 0 0-1.6H5.8v-8.4H10V21a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1"/>
                  <path fill="currentColor" d="M33 8H10v2h23a1 1 0 0 0 0-2"/>
                  <path fill="currentColor" d="M33 18H18v2h15a1 1 0 0 0 0-2"/>
                  <path fill="currentColor" d="M33 28H18v2h15a1 1 0 0 0 0-2"/>
                </svg>
                <span className="mobile-filter-button-label">Organize</span>
              </button>
              {SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS && (filter === 'all' || filter === 'incomplete') && (
                <Menu isOpen={isTaskViewMenuOpen} onClose={() => setIsTaskViewMenuOpen(false)} align="left">
                  <MenuItem
                    isActive={filter === 'incomplete'}
                    onClick={() => {
                      setFilter(filter === 'incomplete' ? 'all' : 'incomplete');
                      setIsTaskViewMenuOpen(false);
                    }}
                  >
                    Incomplete
                  </MenuItem>
                  <MenuItem
                    isActive={timeFilter === 'overdue'}
                    onClick={() => {
                      setTimeFilter(timeFilter === 'overdue' ? null : 'overdue');
                      setIsTaskViewMenuOpen(false);
                    }}
                  >
                    Overdue
                  </MenuItem>
                  <MenuItem
                    isActive={timeFilter === 'today'}
                    onClick={() => {
                      setTimeFilter(timeFilter === 'today' ? null : 'today');
                      setIsTaskViewMenuOpen(false);
                    }}
                  >
                    Today
                  </MenuItem>
                  <MenuItem
                    isActive={timeFilter === 'next7days'}
                    onClick={() => {
                      setTimeFilter(timeFilter === 'next7days' ? null : 'next7days');
                      setIsTaskViewMenuOpen(false);
                    }}
                  >
                    Next 7 days
                  </MenuItem>
                </Menu>
              )}
            </div>
            <div className="mobile-filter-button-container" ref={prioritizeMenuRef}>
              <button
                className={`mobile-filter-button text-non-selectable mobile-filter-button-prioritize ${filter === 'prioritize' ? 'active' : ''}`}
                onClick={() => {
                  if (prioritizeJustLongPressedRef.current) {
                    prioritizeJustLongPressedRef.current = false;
                    return;
                  }
                  if (filter === 'prioritize') {
                    // On mobile, dropdown opens only via long-press; tap does nothing when active
                  } else {
                    trackEvent('prioritize_clicked');
                    setFilter('prioritize');
                  }
                }}
                onTouchStart={() => {
                  if (SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS && filter === 'prioritize') {
                    clearPrioritizeLongPressTimer();
                    prioritizeLongPressTimerRef.current = setTimeout(() => {
                      prioritizeLongPressTimerRef.current = null;
                      prioritizeJustLongPressedRef.current = true;
                      setIsPrioritizeMenuOpen(true);
                    }, LONG_PRESS_MS);
                  }
                }}
                onTouchEnd={clearPrioritizeLongPressTimer}
                onTouchCancel={clearPrioritizeLongPressTimer}
                aria-label="Prioritize"
              >
                <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path fill="currentColor" d="M216 48H40a8 8 0 0 0-8 8v152a16 16 0 0 0 16 16h40a16 16 0 0 0 16-16v-48h48v16a16 16 0 0 0 16 16h40a16 16 0 0 0 16-16V56a8 8 0 0 0-8-8M88 208H48v-80h40Zm0-96H48V64h40Zm64 32h-48V64h48Zm56 32h-40v-48h40Zm0-64h-40V64h40Z"/>
                </svg>
                <span className="mobile-filter-button-label">Prioritize</span>
              </button>
              {SHOW_ORGANIZE_PRIORITIZE_DROPDOWNS && filter === 'prioritize' && (
                <Menu isOpen={isPrioritizeMenuOpen} onClose={() => setIsPrioritizeMenuOpen(false)} align="left">
                  {PRIORITIZE_SECTIONS.map(({ key, label }) => (
                    <MenuItem
                      key={key}
                      isActive={visiblePrioritizeSections.has(key)}
                      onClick={() => {
                        togglePrioritizeSectionVisibility(key);
                        setIsPrioritizeMenuOpen(false);
                      }}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </div>
            </div>
          </div>
          <div className="mobile-header-right">
            <button
              type="button"
              className="mobile-header-help-button text-non-selectable"
              onClick={handleHelpClick}
              aria-label="Help"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="1.5" d="M11.83 14v-.621a2.33 2.33 0 0 1 1.164-2.02c1.126-.66 1.514-2.057.854-3.183s-2.057-1.514-3.183-.854C9.965 7.75 9.5 8.487 9.5 9.341M12 17h-.008M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10s10-4.477 10-10"/></svg>
            </button>
            <div className="mobile-user-pill-container" ref={userPillRef}>
              <button
                type="button"
                className={`mobile-user-pill text-non-selectable ${isUserMenuOpen ? 'active' : ''}`}
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-label={isLoggedIn ? (userEmail || 'Account') : 'Account'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="6" r="4"/>
                    <path d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5Z"/>
                  </g>
                </svg>
                <span className="mobile-user-pill-text">{isLoggedIn ? (userEmail || 'User') : 'Account'}</span>
              </button>
            {isUserMenuOpen && isLoggedIn && (
              <UserMenu
                userEmail={userEmail}
                onLogout={logout}
                onHelpClick={handleHelpClick}
                onAboutClick={handleAboutClick}
                onFeedbackClick={handleFeedbackClick}
                onInviteCodeClick={() => {
                  setIsInviteCodeModalOpen(true);
                  setIsUserMenuOpen(false);
                }}
              />
            )}
            {isUserMenuOpen && !isLoggedIn && (
              <Menu isOpen={isUserMenuOpen} onClose={() => setIsUserMenuOpen(false)} align="right">
                <MenuItem
                  onClick={() => {
                    setAuthModalMode('login');
                    setIsAuthModalOpen(true);
                    setIsUserMenuOpen(false);
                  }}
                >
                  Log in
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    const saveButton = document.querySelector('[data-save-button]');
                    if (saveButton && !saveButton.disabled) {
                      saveButton.click();
                    } else {
                      setAuthModalMode('signup');
                      setIsAuthModalOpen(true);
                    }
                  }}
                >
                  Sign up
                </MenuItem>
              </Menu>
            )}
            </div>
          </div>
        </div>
        <Sidebar isMobileOpen={isMobileSidebarOpen} onMobileClose={handleSidebarClose} />
        <main>
          <TaskTable />
        </main>
      </div>
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
      <InviteCodeModal isOpen={isInviteCodeModalOpen} onClose={() => setIsInviteCodeModalOpen(false)} />
      <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
      <SignupLoginModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        tasks={[]}
        initialMode={authModalMode}
        loginOnly={false}
        source="header_cta"
      />
    </>
  );

  return (
    <HelpShowcaseProvider openHelpShowcase={() => setIsHelpShowcaseOpen(true)}>
      {content}
      {isHelpShowcaseOpen && (
        <FeatureShowcase onClose={() => setIsHelpShowcaseOpen(false)} />
      )}
    </HelpShowcaseProvider>
  );
}

/** Wrapper for route that provides list id from URL (e.g. /list/123). */
function AppListRoute() {
  const { id } = useParams();
  const location = useLocation();
  return (
    <AppWithProviders
      listIdFromUrl={id}
      fromSidebarNavigation={location.state?.fromSidebar === true}
    />
  );
}

/** Provides TasksProvider with presetSlug and optional listIdFromUrl / fromSidebarNavigation. */
function AppWithProviders({ listIdFromUrl = null, fromSidebarNavigation = false }) {
  const location = useLocation();
  const presetSlug = getPresetSlugFromPath(location.pathname);

  return (
    <TasksProvider
      presetSlug={presetSlug}
      listIdFromUrl={listIdFromUrl}
      fromSidebarNavigation={fromSidebarNavigation}
    >
      <FilterProvider>
        <PrioritizeSectionsProvider>
          <ImportPopupProvider>
            <AppContent />
          </ImportPopupProvider>
        </PrioritizeSectionsProvider>
      </FilterProvider>
    </TasksProvider>
  );
}

function App() {
  const location = useLocation();
  const presetSlug = getPresetSlugFromPath(location.pathname);
  const forceLoggedOut = Boolean(presetSlug);

  return (
    <AuthProvider forceLoggedOut={forceLoggedOut}>
      <Routes>
        <Route path="/" element={<AppWithProviders />} />
        <Route path="/list/:id" element={<AppListRoute />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;

